import { useIntl } from 'react-intl';

import { useAxesConfig } from './useAxesConfig.js';
import { useColorPalette } from './useColorPalette.js';
import { useSortLinearDatapoints } from './useSortLinearDatapoints.js';
import { useSortRectangleDatapoints } from './useSortRectangleDatapoints.js';
import {
  type ConcreteStrategy,
  getStrategyFactory,
} from '../processing-strategies/factory/factory.js';
import { AreaSeriesStrategy } from '../processing-strategies/strategies/area-series.js';
import { BarSeriesStrategy } from '../processing-strategies/strategies/bar-series.js';
import { DotSeriesStrategy } from '../processing-strategies/strategies/dot-series.js';
import { LineSeriesStrategy } from '../processing-strategies/strategies/line-series.js';
import { RectSeriesStrategy } from '../processing-strategies/strategies/rect-series.js';
import type { MutableProcessingContext } from '../processing-strategies/types/processing-types.js';
import { getDatumValue } from '../processing-strategies/utils/access-datapoint/get-datapoints.js';
import { useFilteredSeriesIdsState } from '../store/selectors.js';
import { isCategorical } from '../types/guards.js';
import type {
  AxisIdToScaleDomain,
  MapDatapoints,
  RectDatapointInternal,
  SeriesToMetadata,
  XYChartAxisInternal,
  XYChartSeriesWithDatapoints,
  XYChartVariantsInternal,
} from '../types/xy-chart-internals.js';
import type { XYChartData } from '../types/xy-chart.js';
import {
  isInfiniteZoomEnabledFor,
  isPanEnabledFor,
  isZoomEnabledFor,
} from '../utils/axis-interactions.js';
import { combineDataWithCorrespondingSeries } from '../utils/combine-data-with-corresponding-series.js';
import { combineSeriesWithAxes } from '../utils/combine-series-with-axes.js';

/**
 * A hook to process chart data and configure axes for an XY chart.
 *
 * @param seriesVariantsProps - The array of series variant properties.
 * @param data - The array of chart data.
 * @returns - An object containing axes, series with data points, rect data points to metadata mapping, axis ID to scale domain mapping, and series ID to value scale domain mapping.
 * @returns axes - The array of valid axes configurations.
 * @returns seriesWithDatapoints - The array of series with data points.
 * @returns rectSeriesToMetadata - A mapping of rect series to their metadata.
 * @returns axisIdToScaleDomain - A mapping of axis IDs to their scale domains.
 * @returns seriesIdToValueScaleDomain - A mapping of series IDs to their value scale domains.
 */
export function useXYChartDataProvider(
  seriesVariantsProps: XYChartVariantsInternal[],
  data: XYChartData[],
) {
  const axes = useAxesConfig();
  const colorPalette = useColorPalette();
  const intl = useIntl();

  const axisIdToScaleDomain: AxisIdToScaleDomain = new Map();
  const axisWithSeries: Set<string> = new Set();
  const datapointMetadataMap: MapDatapoints = new Map();
  const axisIdToCategoryDatum = new Map<string, Map<string, XYChartData>>();

  const {
    addValue,
    getCurrentSelectConfig: getCurrentShapeSelectConfig,
    searchAllAxisDatapoints: searchAllAxisShapeDatapoints,
  } = useSortLinearDatapoints(seriesVariantsProps, datapointMetadataMap);
  const {
    addValue: addRectangleValue,
    searchAllAxisDatapoints: searchAllAxisRectangleDatapoints,
    getCurrentSelectConfig: getCurrentRectangleSelectConfig,
  } = useSortRectangleDatapoints(seriesVariantsProps);

  const seriesWithAxes = combineSeriesWithAxes(seriesVariantsProps, axes);
  const dataWithSeries = combineDataWithCorrespondingSeries(
    data,
    seriesWithAxes,
  );

  const seriesWithDatapoints: Array<XYChartSeriesWithDatapoints> = [];
  const seriesToMetadata: SeriesToMetadata = new Map();

  const seriesMap = new Map();

  const seriesIndexes: string[] = [];

  const rectSeriesStrategy = new RectSeriesStrategy({
    addValue: addRectangleValue,
  });
  const dotSeriesStrategy = new DotSeriesStrategy({ addValue });
  const lineSeriesStrategy = new LineSeriesStrategy({ addValue });
  const barSeriesStrategy = new BarSeriesStrategy({
    addValue: addRectangleValue,
  });
  const areaSeriesStrategy = new AreaSeriesStrategy({ addValue });
  const strategies: ConcreteStrategy[] = [
    rectSeriesStrategy,
    dotSeriesStrategy,
    lineSeriesStrategy,
    barSeriesStrategy,
    areaSeriesStrategy,
  ];

  const filteredSeriesIds = useFilteredSeriesIdsState();

  const mutableContext: MutableProcessingContext = {
    seriesMap,
    seriesWithDatapoints,
    seriesToMetadata,
    axisIdToScaleDomain,
    seriesIndexes,
    intl,
    colorPalette,
    datapointMetadataMap,
    filteredSeriesIds,
    axisWithSeries,
  };

  const captureCategoryDatum = (
    axis: XYChartAxisInternal,
    accessor: string | undefined,
    datum: XYChartData,
  ) => {
    if (!isCategorical(axis.type) || !accessor) {
      return;
    }

    const rawValue = getDatumValue(datum, accessor);
    if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
      return;
    }
    const raw = String(rawValue);

    let perAxis = axisIdToCategoryDatum.get(axis.id);
    if (!perAxis) {
      perAxis = new Map<string, XYChartData>();
      axisIdToCategoryDatum.set(axis.id, perAxis);
    }
    if (!perAxis.has(raw)) {
      perAxis.set(raw, datum);
    }
  };

  const groupsWithStrategies = dataWithSeries
    .filter(({ series }) => series.length)
    .map(({ data, series }) => {
      const seriesWithStrategy = series.map((entry) => ({
        ...entry,
        strategy: getStrategyFactory(strategies, entry.seriesProps),
      }));

      let hasCategoricalAxis = false;
      for (const { xAxis, yAxis } of seriesWithStrategy) {
        if (isCategorical(xAxis.type) || isCategorical(yAxis.type)) {
          hasCategoricalAxis = true;
          break;
        }
      }

      return { data, seriesWithStrategy, hasCategoricalAxis };
    });

  for (const { data, seriesWithStrategy } of groupsWithStrategies) {
    for (const { seriesProps, xAxis, yAxis, strategy } of seriesWithStrategy) {
      if (strategy?.preprocess) {
        strategy.preprocess(data, seriesProps, xAxis, yAxis, mutableContext);
      }
    }
  }

  for (const {
    data,
    seriesWithStrategy,
    hasCategoricalAxis,
  } of groupsWithStrategies) {
    for (const datum of data) {
      for (const {
        seriesProps,
        xAxis,
        yAxis,
        strategy,
      } of seriesWithStrategy) {
        if (strategy) {
          const processResult = strategy.process(
            datum,
            seriesProps,
            xAxis,
            yAxis,
            mutableContext,
          );

          if (processResult === 'continue') {
            continue;
          }
        } else {
          throw new Error(
            `No strategy found to process series with id: ${seriesProps.id}`,
          );
        }

        if (hasCategoricalAxis) {
          captureCategoryDatum(xAxis, seriesProps.x0Accessor, datum);
          captureCategoryDatum(yAxis, seriesProps.y0Accessor, datum);
        }
      }
    }
  }

  const validAxes = axes.filter(
    (axis) => axisIdToScaleDomain.has(axis.id) && axisWithSeries.has(axis.id),
  );
  const linearSelectionContextValue = {
    selectionConfigByAxisId: getCurrentShapeSelectConfig(),
    searchAllAxisDatapoints: searchAllAxisShapeDatapoints,
  };
  const rectSelectionContextValue = {
    selectionConfigByAxisId: getCurrentRectangleSelectConfig(),
    searchAllAxisDatapoints: searchAllAxisRectangleDatapoints,
  };
  return {
    axesMetadata: {
      isZoomEnabled: !!validAxes.find(isZoomEnabledFor),
      isPanEnabled: !!validAxes.find(isPanEnabledFor),
      isInfinite: !!validAxes.find(isInfiniteZoomEnabledFor),
    },
    axes: validAxes,
    axisIdToScaleDomain,
    axisIdToCategoryDatum,
    seriesIdToValueScaleDomain: rectSeriesStrategy.seriesIdToValueScaleDomain,
    datapointMetadataMap,
    linearSelectionContextValue,
    rectSelectionContextValue,
    seriesWithDatapoints,
    filteredSeriesWithDatapoints: seriesWithDatapoints.filter((series) => {
      if (series.type === 'rect') {
        series.filteredData = series.data.filter(
          (datapoint: RectDatapointInternal) => {
            return datapointMetadataMap.get(datapoint)?.isVisible !== false;
          },
        );
      }

      return seriesToMetadata.get(series)?.isVisible !== false;
    }),
    seriesToMetadata,
    axisIdToDimension: barSeriesStrategy.axisIdToDimension,
  };
}
